import { Combobox } from '@headlessui/react';
import { Button, Input } from '@insify/design-system/components';
import { Search as SearchIcon } from '@insify/design-system/icons';
import { useDebounce } from '@insify/react-hooks';
import { makeInputContext, makeInputValueContext } from '@objectiv/schema';
import { useInputChangeEventTracker } from '@objectiv/tracker-react';
import Fuse from 'fuse.js';
import { useEffect } from 'react';

import { showMoreButton, showMoreButtonWrapper } from './Search.module.css';

import FuseResultMatch = Fuse.FuseResultMatch;
import type { ObjectivValueTrackingProps } from '@objectiv/tracker-react';
import type { ChangeEventHandler } from 'react';
import type React from 'react';

export type OptionRenderPropArg = {
  active?: boolean;
  selected?: boolean;
  disabled?: boolean;
};

export type SearchResultMatch = FuseResultMatch;
export type SearchResultItem<Model> = { item: Model; matches: ReadonlyArray<SearchResultMatch> };
export type SearchItemComponentType<Model> = React.ComponentType<
  { className?: string } & SearchResultItem<Model> & OptionRenderPropArg
>;

type Props<Model> = {
  searchString?: string;
  selected: Model | null;
  items: SearchResultItem<Model>[];
  isLoading: boolean;
  disabled?: boolean;
  translations: {
    showMore: string;
    searchLabel?: string;
    searchTooltip?: string;
    searchPlaceholder?: string;
  };
  onSelect: (item: Model) => void;
  onChange: ChangeEventHandler<HTMLInputElement>;
  getKey: (item: Model) => string;
  inputRef?: React.RefObject<HTMLInputElement>;
  Item: SearchItemComponentType<Model>;
  hasNoResults: boolean;
  noResult: JSX.Element;
  hasPagination?: boolean;
  hasFocusOnMount?: boolean;
  showMoreResults?: () => void;
  searchResultListClassName?: string;
  searchActions?: React.ReactNode;
  name: string;
} & {
  trackingProps?: ObjectivValueTrackingProps;
};

const TRACKING_ID = 'search';

const cancelDefaultTabAndEscape: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
  if (e.code === 'Tab' || e.code === 'Escape') {
    e.preventDefault();
  }
};

export const Search = <Model,>({
  searchString,
  selected,
  items,
  isLoading,
  disabled,
  translations,
  onSelect,
  onChange,
  getKey,
  Item,
  hasNoResults,
  noResult,
  hasPagination,
  inputRef,
  showMoreResults,
  searchResultListClassName,
  searchActions,
  name,
  trackingProps,
}: Props<Model>): JSX.Element => {
  const open = !!searchString?.length;

  const trackInputChange = useInputChangeEventTracker({
    locationStack: [makeInputContext({ id: TRACKING_ID })],
  });

  const debouncedSearchString = useDebounce(searchString, 200);

  useEffect(() => {
    if (debouncedSearchString) {
      void trackInputChange({
        globalContexts: [
          makeInputValueContext({
            id: TRACKING_ID,
            value: debouncedSearchString,
          }),
        ],
      });
    }
  }, [debouncedSearchString]);

  const content = () => {
    const actions = searchActions ?? null;

    if (open)
      return (
        <>
          {items.map((item) => (
            <Combobox.Option key={getKey(item.item)} value={item.item}>
              {(optionProps) => <Item {...optionProps} item={item.item} matches={item.matches} />}
            </Combobox.Option>
          ))}
          {hasNoResults && noResult}
          {actions}
          {hasPagination && !hasNoResults && (
            <li className={showMoreButtonWrapper}>
              <Button
                type='button'
                kind='secondary'
                className={showMoreButton}
                trackingId='showMore'
                loading={isLoading}
                onClick={showMoreResults}
              >
                {translations.showMore}
              </Button>
            </li>
          )}
        </>
      );

    if (selected)
      return (
        <>
          <Combobox.Option key={getKey(selected)} value={selected}>
            <Item selected={true} active={false} item={selected} matches={[]} />
          </Combobox.Option>
          {actions}
        </>
      );

    return actions;
  };

  return (
    <Combobox value={selected} onChange={onSelect} disabled={disabled}>
      <Combobox.Input
        name={name}
        data-testid='searchInput'
        ref={inputRef}
        as={Input}
        onKeyDown={cancelDefaultTabAndEscape}
        trackingId={TRACKING_ID}
        trackingProps={trackingProps}
        before={<SearchIcon />}
        placeholder={translations.searchPlaceholder}
        loading={isLoading}
        value={searchString}
        onChange={onChange}
        label={translations.searchLabel}
        tooltip={translations.searchTooltip}
        hasNoBottomContent={true}
      />
      <Combobox.Options className={searchResultListClassName} static>
        {content()}
      </Combobox.Options>
    </Combobox>
  );
};
